import {
  SandboxClient,
  SandboxHost,
  SandboxSession,
  SandboxState,
  SandboxStore,
} from '../../store';
import { activeWindowForHost } from '../../store/hostApplications';
import { SandboxOperationHandler } from '../types';
import { inputObject } from './utils';

interface SandboxHostView extends SandboxHost {
  user: SandboxClient | null;
  client_sessions: Array<Record<string, unknown>>;
  bookings: Array<Record<string, unknown>>;
}

const sessionView = (
  state: SandboxState,
  session: SandboxSession,
  currentTimestamp: string
): Record<string, unknown> => {
  const startedAt = Date.parse(session.started_at);
  const currentTime = Date.parse(currentTimestamp);
  const elapsedSinceStart =
    Number.isFinite(startedAt) && Number.isFinite(currentTime)
      ? Math.max(0, Math.floor((currentTime - startedAt) / 1000))
      : session.elapsed;
  const elapsed = session.postpaid
    ? Math.max(session.elapsed, elapsedSinceStart)
    : Math.min(session.duration, Math.max(session.elapsed, elapsedSinceStart));
  const currentSession = {
    ...session,
    elapsed,
    time_left: session.postpaid ? 0 : Math.max(0, session.duration - elapsed),
  };

  return {
    ...currentSession,
    user: currentSession.client_uuid
      ? state.clients.find((client) => client.uuid === session.client_uuid) ??
        null
      : null,
    hosts: [
      ...currentSession.host_ids.map((hostId) => ({
        started_at: currentSession.started_at,
        finished_at:
          hostId === currentSession.host_id
            ? currentSession.finished_at
            : currentSession.started_at,
        host: (() => {
          const host = state.hosts.find((candidate) => candidate.id === hostId);
          return host
            ? {
                ...host,
                counters: {
                  ...host.counters,
                  active_window: activeWindowForHost(hostId, state.sessions),
                },
              }
            : undefined;
        })(),
      })),
    ],
  };
};

export const hostView = (
  state: SandboxState,
  id: number,
  currentTimestamp: string
): SandboxHostView => {
  const host = state.hosts.find((item) => item.id === id);
  if (!host) throw new Error('Хост не найден');
  const sessions = state.sessions.filter(
    (session) => session.host_id === id && session.status === 'ACTIVE'
  );
  const userUuid = sessions[0]?.client_uuid ?? host.shell_client_uuid;
  const user = userUuid
    ? state.clients.find((client) => client.uuid === userUuid) ?? null
    : null;
  return {
    ...host,
    counters: {
      ...host.counters,
      active_window: activeWindowForHost(host.id, state.sessions),
    },
    user,
    client_sessions: sessions.map((session) =>
      sessionView(state, session, currentTimestamp)
    ),
    bookings: state.bookings
      .filter(
        (booking) =>
          booking.hostIds.includes(id) &&
          !['CANCELED', 'FINISHED'].includes(booking.status)
      )
      .map((booking) => ({ ...booking, hosts: booking.hostIds })),
  };
};

const allHosts = (
  state: SandboxState,
  currentTimestamp: string
): SandboxHostView[] =>
  state.hosts.map((host) => hostView(state, host.id, currentTimestamp));
const groups = (state: SandboxState): SandboxState['hostGroups'] =>
  state.hostGroups;
const id = (variables: Record<string, unknown>): number =>
  Number(variables.id ?? inputObject(variables).id);
const hostViewState = (
  store: SandboxStore
): { state: SandboxState; currentTimestamp: string } => ({
  state: store.getState(),
  currentTimestamp: store.getCurrentTimestamp(),
});

export const hostResolvers: Record<string, SandboxOperationHandler> = {
  hostsData: (_, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return {
      hostsOverview: allHosts(state, currentTimestamp),
      hostGroupsOverview: groups(state),
    };
  },
  hostsStatuses: (_, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return { hostsOverview: allHosts(state, currentTimestamp) };
  },
  hostsModalsData: (_, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return {
      hostsOverview: allHosts(state, currentTimestamp),
      hostGroupsOverview: groups(state),
    };
  },
  hostOverview: (variables, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return { hostOverview: hostView(state, id(variables), currentTimestamp) };
  },
  hostData: (variables, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return { hostOverview: hostView(state, id(variables), currentTimestamp) };
  },
  mapHostData: (variables, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return { hostOverview: hostView(state, id(variables), currentTimestamp) };
  },
  draggableHostData: (variables, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return { hostOverview: hostView(state, id(variables), currentTimestamp) };
  },
  draggableHostGroupData: (variables, store) => ({
    hostGroupOverview:
      groups(store.getState()).find((group) => group.id === id(variables)) ??
      null,
  }),
  hostGroups: (_, store) => ({
    hostGroups: groups(store.getState()),
    hostGroupsOverview: groups(store.getState()),
  }),
  sidebarData: (variables, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return { hostOverview: hostView(state, id(variables), currentTimestamp) };
  },
  hostsByIds: (variables, store) => {
    const ids = (variables.ids as number[] | undefined) ?? [];
    const { state, currentTimestamp } = hostViewState(store);
    return {
      hostsByIds: ids.map((hostId) =>
        hostView(state, Number(hostId), currentTimestamp)
      ),
    };
  },
  hostTypes: () => ({
    hostTypes: [
      'PC',
      'CONSOLE',
      'VR',
      'BILLIARD',
      'BOARD_GAME',
      'RACING_SIM',
    ].map((hostId) => ({ id: hostId })),
  }),
  hostTitles: (_, store) => ({
    hostGroupTitles: groups(store.getState()).map(
      ({ id, title, priority }) => ({
        id,
        title,
        priority,
      })
    ),
  }),
  createHost: (variables, store) => ({
    createHost: store.createHost(inputObject(variables)),
  }),
  updateHost: (variables, store) => ({
    updateHost: store.updateHost(
      Number(variables.id ?? inputObject(variables).id),
      inputObject(variables)
    ),
  }),
  setServiceMode: (variables, store) => {
    const input = inputObject(variables);
    const flag = variables.flag ?? input.flag;
    const value =
      typeof flag === 'object' && flag !== null && 'value' in flag
        ? Boolean((flag as { value: unknown }).value)
        : Boolean(flag);
    const host = store.setServiceMode(Number(variables.id ?? input.id), value);
    return { setHostServiceMode: host };
  },
  sendWakePacket: (variables, store) => {
    const hostId = Number(variables.id ?? inputObject(variables).host_id ?? 0);
    if (hostId)
      store.createCommand({ host_id: hostId, action: 'SEND_WAKE_PACKET' });
    return { sendWakePacket: true };
  },
  sendWakePackets: (variables, store) => {
    const ids =
      (variables.ids as number[] | undefined) ??
      (inputObject(variables).host_ids as number[] | undefined) ??
      [];
    if (ids.length)
      store.createCommand({ host_ids: ids, action: 'SEND_WAKE_PACKET' });
    return { sendWakePackets: true };
  },
  createCommand: (variables, store) => ({
    createCommand: store.createCommand(inputObject(variables)),
  }),
  createCommands: (variables, store) => {
    const input = inputObject(variables);
    const commands = (
      (input.commands as Array<Record<string, unknown>> | undefined) ?? [input]
    ).map((command) => store.createCommand(command));
    return { createCommands: { commands } };
  },
  moveHosts: (variables, store) => {
    const input =
      (variables.input as
        | Array<{ id: number; coord_x?: number; coord_y?: number }>
        | undefined) ?? [];
    return { updateHosts: store.moveHosts(input) };
  },
  changeHostsData: (_, store) => {
    const { state, currentTimestamp } = hostViewState(store);
    return {
      hostsOverview: allHosts(state, currentTimestamp),
      hostGroupsOverview: groups(state),
    };
  },
  freeHosts: (variables, store) => {
    const hostId = Number(
      variables.host_id ?? inputObject(variables).host_id ?? 0
    );
    return {
      freeHosts: allHosts(store.getState(), store.getCurrentTimestamp())
        .filter(
          (host) =>
            !host.client_sessions.length && (!hostId || host.id !== hostId)
        )
        .map((host) => ({
          ...host,
          group:
            groups(store.getState()).find(
              (group) => group.id === host.group_id
            ) ?? null,
        })),
    };
  },
  changeClientSessionHost: (variables, store) => {
    const input = inputObject(variables);
    const session = store.moveSession(
      Number(variables.id ?? input.id),
      Number(input.host_id)
    );
    return {
      changeClientSessionHost: sessionView(
        store.getState(),
        session,
        store.getCurrentTimestamp()
      ),
    };
  },
  startClientSession: (variables, store) => {
    const input = inputObject(variables);
    const session = store.startSession({
      host_id: Number(input.host_id),
      client_uuid: (input.client_uuid as string | undefined) ?? null,
      post_paid: Boolean(input.post_paid),
      tariff_id: input.tariff_id ? Number(input.tariff_id) : undefined,
    });
    return {
      startClientSession: sessionView(
        store.getState(),
        session,
        store.getCurrentTimestamp()
      ),
    };
  },
  authenticateShellClient: (variables, store) => {
    const host = store.setShellClient(
      Number(variables.hostId),
      String(variables.clientUuid)
    );

    return {
      authenticateShellClient: hostView(
        store.getState(),
        host.id,
        store.getCurrentTimestamp()
      ),
    };
  },
  stopClientSession: (variables, store) => {
    const input = inputObject(variables);
    const { session, payment } = store.stopSessionWithPayment(
      Number(variables.id ?? input.id)
    );
    return {
      stopClientSession: {
        ...sessionView(store.getState(), session, store.getCurrentTimestamp()),
        payment,
      },
    };
  },
};
